import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Chip, Avatar,
  List, ListItem, ListItemAvatar, ListItemText, Divider
} from '@mui/material';
import { People, Business, FolderSpecial, Warning } from '@mui/icons-material';
import { usersAPI, clientsAPI, projectsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { INDESAP } from '../../theme';
import DevBadge from '../../components/common/DevBadge';

const StatCard = ({ icon, label, value, color }) => (
  <Card>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
      <Avatar sx={{ bgcolor: `${color}18`, width: 54, height: 54 }}>
        {React.cloneElement(icon, { sx: { color, fontSize: 28 } })}
      </Avatar>
      <Box>
        <Typography variant="h4" fontWeight={700}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Box>
    </CardContent>
  </Card>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState({ users: 0, clients: 0, projects: 0 });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [usersRes, clientsRes, projectsRes] = await Promise.all([
          usersAPI.getAll(),
          clientsAPI.getAll(),
          projectsAPI.getAll()
        ]);
        setStats({
          users: usersRes.data.length,
          clients: clientsRes.data.length,
          projects: projectsRes.data.length
        });
        setUsers(usersRes.data.slice(0, 8));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <DevBadge id="page:admin-dashboard" />
      <Typography variant="h5" fontWeight={700} mb={0.5}>Panel de Administración</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Vista general del sistema INDESAP
      </Typography>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={4}>
          <StatCard icon={<People />} label="Usuarios Registrados" value={stats.users} color={INDESAP.navy} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard icon={<Business />} label="Empresas Cliente" value={stats.clients} color={INDESAP.info} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard icon={<FolderSpecial />} label="Proyectos Activos" value={stats.projects} color={INDESAP.success} />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Usuarios Recientes</Typography>
              <List disablePadding>
                {users.map((u, idx) => (
                  <React.Fragment key={u._id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: INDESAP.navy, fontSize: '0.85rem' }}>
                          {(u.name?.[0] || u.email[0]).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={u.name ? `${u.name} ${u.lastName || ''}` : u.email}
                        secondary={u.email}
                      />
                      <Box display="flex" gap={1} alignItems="center">
                        <Chip
                          label={u.role === 'superadmin' ? 'Admin' : u.role === 'teamlead' ? 'Líder' : 'Miembro'}
                          size="small"
                          color={u.role === 'superadmin' ? 'error' : u.role === 'teamlead' ? 'primary' : 'default'}
                        />
                        <Chip
                          label={u.status === 'active' ? 'Activo' : u.status === 'invited' ? 'Invitado' : 'Inactivo'}
                          size="small"
                          color={u.status === 'active' ? 'success' : u.status === 'invited' ? 'warning' : 'error'}
                          variant="outlined"
                        />
                      </Box>
                    </ListItem>
                    {idx < users.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Accesos Rápidos</Typography>
              {[
                { label: 'Gestionar Usuarios', path: '/admin/users', color: INDESAP.navy },
                { label: 'Gestionar Clientes', path: '/admin/clients', color: INDESAP.info },
                { label: 'Gestionar Categorías', path: '/admin/categories', color: INDESAP.success }
              ].map(item => (
                <Box key={item.path} sx={{
                  p: 2, mb: 1.5, borderRadius: 2, bgcolor: `${item.color}10`,
                  cursor: 'pointer', border: `1px solid ${item.color}22`,
                  '&:hover': { bgcolor: `${item.color}18` },
                  transition: 'all 0.15s'
                }}
                  onClick={() => window.location.href = item.path}
                >
                  <Typography variant="body2" fontWeight={600} color={item.color}>{item.label}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
